import React, { useState } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import { r2DeletionService } from '../services/r2DeletionService';

interface DeleteTacticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tacticsId: string;
  tacticsName: string;
  onSuccess: () => void;
  onToast?: (message: string, type: 'success' | 'error') => void;
}

const DeleteTacticsModal: React.FC<DeleteTacticsModalProps> = ({
  isOpen,
  onClose,
  tacticsId,
  tacticsName,
  onSuccess,
  onToast,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setError(null);
    setIsDeleting(true);

    try {
      const deleteResult = await r2DeletionService.deleteTacticsAsset(tacticsId);

      if (!deleteResult.success) {
        throw new Error(deleteResult.error || 'Failed to delete tactics image');
      }

      // Show success toast
      if (onToast) {
        onToast('Tactics image deleted successfully', 'success');
      }

      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete tactics image';
      setError(errorMessage);
      if (onToast) {
        onToast(errorMessage, 'error');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Tactics Image">
      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-700">
          Are you sure you want to delete <strong>"{tacticsName}"</strong>?
        </p>
        <p className="text-xs text-gray-500">
          This action cannot be undone. The tactics image will be permanently deleted from storage.
        </p>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Deleting...' : 'Delete Image'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteTacticsModal;

